import {
	AlertCircle,
	CheckCircle2,
	ChevronRight,
	Clock,
	Play,
} from "lucide-react";
import { useMemo, useState } from "react";
import { CapabilityBrowser } from "@/components/connectors/capability-browser";
import { Button } from "@/components/ui/button";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { FormDraftConflict } from "@/components/ui/form-draft-conflict";
import { Spinner } from "@/components/ui/spinner";
import { useFormDraft } from "@/hooks/use-form-draft";
import type { ExecuteActionResult } from "@/lib/api";
import { useAvailableOperations, useExecuteAction } from "@/lib/api";
import {
	hasInvalidFormInput,
	serializeFormInputs,
} from "@/lib/form-input-draft";
import { PolicySettingsLink, PolicyStatus } from "./connector-panels";
import {
	createConnectorDraft,
	DynamicConnectorForm,
} from "./dynamic-connector-form";

interface ActionRunnerProps {
	connectionId: number;
	browse?: boolean;
	onReviewPermissions?: () => void;
	ownerSlug?: string;
	connectorKey?: string;
	/** Connection-scoped discovery includes blocked operations for human inspection. */
	policyOperations?: ReturnType<typeof useAvailableOperations>["data"];
}

export function ActionRunner({
	connectionId,
	ownerSlug,
	connectorKey,
	policyOperations,
	browse = false,
	onReviewPermissions,
}: ActionRunnerProps) {
	const { data: operations } = useAvailableOperations(connectionId);
	const executable = useMemo(
		() => (operations ?? []).filter((operation) => operation.kind === "write"),
		[operations],
	);
	const executableByKey = useMemo(
		() => new Map(executable.map((op) => [op.operation_key, op])),
		[executable],
	);
	const entries = useMemo(() => {
		if (!policyOperations) return executable;
		return policyOperations.map(
			(op) => executableByKey.get(op.operation_key) ?? op,
		);
	}, [policyOperations, executable, executableByKey]);
	const outputPreviews = useMemo(
		() =>
			Object.fromEntries(
				entries.map((operation) => [
					operation.operation_key,
					createConnectorDraft(operation.output_schema, {}, true),
				]),
			),
		[entries],
	);
	const { initialInputs, inputSchemas } = useMemo(
		() => ({
			initialInputs: Object.fromEntries(
				entries.map((operation) => [
					operation.operation_key,
					createConnectorDraft(operation.input_schema),
				]),
			),
			inputSchemas: Object.fromEntries(
				entries.map((operation) => [
					operation.operation_key,
					operation.input_schema,
				]),
			),
		}),
		[entries],
	);
	const draft = useFormDraft(connectionId, initialInputs, inputSchemas);
	const { values: actionInputs, setValues: setActionInputs } = draft;
	const [actionResults, setActionResults] = useState<
		Record<string, ExecuteActionResult>
	>({});
	const [runningAction, setRunningAction] = useState<string | null>(null);
	const executeAction = useExecuteAction();

	const handleInputChange = (
		actionKey: string,
		values: Record<string, unknown>,
	) => {
		setActionInputs((prev) => ({ ...prev, [actionKey]: values }));
	};

	const handleRun = async (actionKey: string) => {
		if (draft.hasConflict || hasInvalidFormInput(actionInputs[actionKey]))
			return;
		setRunningAction(actionKey);
		setActionResults((prev) => {
			const next = { ...prev };
			delete next[actionKey];
			return next;
		});
		try {
			const result = await executeAction.mutateAsync({
				connection_id: connectionId,
				operation_key: actionKey,
				input: serializeFormInputs(actionInputs[actionKey] ?? {}) as Record<
					string,
					unknown
				>,
			});
			setActionResults((prev) => ({ ...prev, [actionKey]: result }));
		} catch (error) {
			setActionResults((prev) => ({
				...prev,
				[actionKey]: {
					run_id: 0,
					status: "failed",
					error_message: error instanceof Error ? error.message : String(error),
				},
			}));
		} finally {
			setRunningAction(null);
		}
	};

	if (entries.length === 0) return null;

	const renderEntry = (
		operation: (typeof entries)[number],
		expanded = false,
	) => {
		const actionKey = operation.operation_key;
		const isRunning = runningAction === actionKey;
		const result = actionResults[actionKey];
		const access = operations?.find((item) => item.operation_key === actionKey);
		const needsAccess =
			access?.readiness === "scope_upgrade_required" ||
			access?.readiness === "auth_required";
		return (
			<Collapsible key={actionKey} open={expanded ? true : undefined}>
				{expanded && (
					<div className="mb-5 space-y-2">
						<h3 className="text-sm font-medium">Provider permissions</h3>
						<p className="text-sm text-muted-foreground">
							{needsAccess
								? "Additional access is needed before this action can run."
								: access?.readiness &&
										access.readiness !== "ready" &&
										access.readiness !== "blocked"
									? `Availability: ${access.readiness.replaceAll("_", " ")}`
									: access?.readiness === "ready"
										? "Provider access is ready."
										: "Provider access is checked when the action runs."}
						</p>
						{needsAccess && onReviewPermissions && (
							<Button variant="outline" size="sm" onClick={onReviewPermissions}>
								Review permissions
							</Button>
						)}
					</div>
				)}

				<div className="flex flex-wrap items-start gap-2 sm:flex-nowrap">
					{/* Description on its own line: inline after the name it ran past
                    the column edge and clipped on every long one. */}
					{!expanded && (
						<CollapsibleTrigger className="flex min-w-0 flex-[1_1_12rem] items-start gap-1.5 text-sm text-left group">
							<ChevronRight className="h-3 w-3 mt-1 text-muted-foreground shrink-0 transition-transform group-data-[state=open]:rotate-90" />
							<span className="min-w-0 flex-1">
								<span className="block font-medium">
									{operation.name ?? actionKey}
								</span>
								{operation.description && (
									<span className="line-clamp-2 text-xs text-muted-foreground group-data-[state=open]:line-clamp-none">
										{operation.description}
									</span>
								)}
							</span>
						</CollapsibleTrigger>
					)}
					{!expanded && (
						<div className="flex items-center gap-2">
							<PolicyStatus effect={operation.policy?.effect} />
							<PolicySettingsLink
								ownerSlug={ownerSlug}
								connectorKey={connectorKey}
								connectionId={connectionId}
							/>
						</div>
					)}
				</div>
				<CollapsibleContent>
					<div
						className={expanded ? "mt-5 space-y-3" : "ml-4 mt-1.5 space-y-3"}
					>
						{draft.revision?.[actionKey] && (
							<div>
								<p className="text-xs text-muted-foreground mb-1">Input</p>
								<DynamicConnectorForm
									schema={draft.revision[actionKey]}
									values={actionInputs[actionKey]}
									onValuesChange={(values) =>
										handleInputChange(actionKey, values)
									}
									fieldIdPrefix={`action-${connectionId}-${actionKey}-`}
								/>
							</div>
						)}
						{operation.output_schema && (
							<div>
								<p className="text-xs text-muted-foreground mb-1">
									Output Schema
								</p>
								<DynamicConnectorForm
									schema={operation.output_schema}
									values={outputPreviews[actionKey]}
									fieldIdPrefix={`action-${connectionId}-${actionKey}-output-`}
									readOnly
								/>
							</div>
						)}
						{executableByKey.has(actionKey) ? (
							<Button
								size="sm"
								variant="outline"
								onClick={() => handleRun(actionKey)}
								disabled={
									needsAccess ||
									!operation.policy ||
									operation.policy.effect === "deny" ||
									draft.hasConflict ||
									hasInvalidFormInput(actionInputs[actionKey]) ||
									isRunning ||
									runningAction !== null
								}
							>
								{isRunning ? (
									<>
										<Spinner size="md" className="mr-1.5" />
										Running...
									</>
								) : (
									<>
										<Play className="mr-1.5 h-3.5 w-3.5" />
										Run
									</>
								)}
							</Button>
						) : null}
						{result && <ActionResult result={result} />}
					</div>
				</CollapsibleContent>
			</Collapsible>
		);
	};
	if (browse)
		return (
			<div className="space-y-3">
				<FormDraftConflict visible={draft.hasConflict} onReload={draft.reset} />
				<CapabilityBrowser
					entries={entries.map((operation) => ({
						id: operation.operation_key,
						name: operation.name ?? operation.operation_key,
						description: operation.description,
						operation,
						status:
							operations?.find(
								(item) => item.operation_key === operation.operation_key,
							)?.readiness === "scope_upgrade_required"
								? "Additional provider access needed"
								: undefined,
					}))}
					label="actions"
					renderControl={({ operation }) => (
						<div className="flex items-center gap-2">
							<PolicyStatus effect={operation.policy?.effect} />
							<PolicySettingsLink
								ownerSlug={ownerSlug}
								connectorKey={connectorKey}
								connectionId={connectionId}
							/>
						</div>
					)}
					renderDetail={({ operation }) => renderEntry(operation, true)}
				/>
			</div>
		);
	return (
		<div className="space-y-1.5">
			<FormDraftConflict visible={draft.hasConflict} onReload={draft.reset} />
			<div className="space-y-2">
				{entries.map((operation) => renderEntry(operation))}
			</div>
		</div>
	);
}

function ActionResult({ result }: { result: ExecuteActionResult }) {
	if (result.status === "completed") {
		return (
			<div className="rounded-md bg-green-50 dark:bg-green-950/30 p-3 text-sm space-y-1">
				<div className="flex items-center gap-1.5 text-green-700 dark:text-green-400 font-medium">
					<CheckCircle2 className="h-3.5 w-3.5" />
					Completed
					<span className="text-xs font-normal text-muted-foreground ml-auto">
						Run #{result.run_id}
					</span>
				</div>
				{result.output && Object.keys(result.output).length > 0 && (
					<pre className="text-xs text-green-800 dark:text-green-300 font-mono whitespace-pre-wrap mt-1">
						{JSON.stringify(result.output, null, 2)}
					</pre>
				)}
			</div>
		);
	}

	if (result.status === "pending_approval") {
		return (
			<div className="rounded-md bg-yellow-50 dark:bg-yellow-950/30 p-3 text-sm">
				<div className="flex items-center gap-1.5 text-yellow-700 dark:text-yellow-400 font-medium">
					<Clock className="h-3.5 w-3.5" />
					Pending Approval
					<span className="text-xs font-normal text-muted-foreground ml-auto">
						Run #{result.run_id}
					</span>
				</div>
				{result.message && (
					<p className="text-xs text-yellow-800 dark:text-yellow-300 mt-1">
						{result.message}
					</p>
				)}
			</div>
		);
	}

	return (
		<div className="rounded-md bg-red-50 dark:bg-red-950/30 p-3 text-sm">
			<div className="flex items-center gap-1.5 text-red-700 dark:text-red-400 font-medium">
				<AlertCircle className="h-3.5 w-3.5" />
				Failed
				{result.run_id > 0 && (
					<span className="text-xs font-normal text-muted-foreground ml-auto">
						Run #{result.run_id}
					</span>
				)}
			</div>
			{result.error_message && (
				<p className="text-xs text-red-800 dark:text-red-300 font-mono mt-1">
					{result.error_message}
				</p>
			)}
		</div>
	);
}
